import { Injectable, computed, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { TranslateService } from '@ngx-translate/core';
import { UIFeedbackService } from '../../shared/services/ui-feedback.service';
import { WorkspaceStore } from '../../shared/services/workspace.store';
import { AuthReadyService } from '@core/services/auth/auth-ready.service';
import {
  OnboardingOwnerType,
  OnboardingPlan,
  WorkspaceOnboarding} from '../../shared/definitions/workspace';
import {
  ONBOARDING_RESERVED_SLUGS,
  ONBOARDING_SLUG_REGEX
} from '../../shared/definitions/workspace';
import {
  isReservedOnboardingSlug,
  sanitizeOnboardingSlug,
} from '../../shared/utils/slug-sanitizer';

/**
 * Workspace-onboarding flow — refactored 2026-05-10 to centre on the
 * "1 property = 1 cloud, shared selectively" mental model that is core
 * to Proporio's identity, then trimmed 2026-06-13 to cut friction, then
 * restored a dedicated first-property gesture (2026-07-30):
 *
 *   1. welcome           — Property Cloud value showcase (was 3 marketing slides)
 *   2. setup             — workspace name + inbox address (merged: was the two
 *                          separate `owner-profile` + `email-slug` screens)
 *   3. first-property    — mini-add (inline address + type → property wizard)
 *                          or skip to the finale
 *   4. complete          — success screen: mailbox + features + dashboard CTA
 *
 * Legacy `concept-cloud`/`concept-mailbox` and the now-merged
 * `owner-profile`/`email-slug` survive only as redirects (see
 * onboarding.routes.ts) so resumed/deep-linked sessions land cleanly.
 */
export const ONBOARDING_STEP_ROUTES: readonly string[] = [
  'welcome',
  'setup',
  'first-property',
  'complete',
] as const;
export const ONBOARDING_STEP_COUNT = ONBOARDING_STEP_ROUTES.length;

/**
 * SessionStorage key flipped on by `markLocallyFinished()` so the
 * `OnboardingCompletedGuard` / `OnboardingAccessGuard` can treat onboarding
 * as done during the brief window between the server write, the Firestore
 * listener refresh, and the next navigation — the `WorkspaceStore` signal
 * is continuously re-emitted from the service, so an in-memory patch alone
 * gets stomped.
 */
export const ONBOARDING_JUST_FINISHED_KEY = 'onboarding-just-finished';

export function isOnboardingJustFinished(): boolean {
  try {
    return sessionStorage.getItem(ONBOARDING_JUST_FINISHED_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearOnboardingJustFinished(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_JUST_FINISHED_KEY);
  } catch {
    /* ignore */
  }
}

export type SlugRejectionReason = 'taken' | 'reserved' | 'invalid';

export interface SlugAvailabilityResult {
  available: boolean;
  reason?: SlugRejectionReason;
}

/**
 * Wrapper around the owner-workspace onboarding callables.
 *
 * All callables are workspace-scoped — the active workspace id is read
 * from the `WorkspaceStore` at call time, so this service never needs to
 * be parameterised. The server is the sole source of truth for
 * `onboarding.currentStep` (monotonic); this service merely dispatches.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly fns = inject(Functions);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly translate = inject(TranslateService);
  private readonly authReady = inject(AuthReadyService);

  /** Current onboarding state projected from the workspace signal. */
  readonly onboarding = computed<WorkspaceOnboarding | null>(() => {
    const ws = this.workspaceStore.currentWorkspace();
    return ws?.onboarding ?? null;
  });

  readonly currentStep = computed<number>(() => this.onboarding()?.currentStep ?? 0);
  readonly completed = computed<boolean>(() => !!this.onboarding()?.completed);
  readonly emailSlug = computed<string | null>(() => this.onboarding()?.emailSlug ?? null);

  /** Route segment (under /onboarding) for the server's current step. */
  stepRoute(step = this.currentStep()): string {
    const clamped = Math.max(0, Math.min(ONBOARDING_STEP_COUNT - 1, step));
    return ONBOARDING_STEP_ROUTES[clamped];
  }

  // --------------------------------------------------------------------------
  // Callable wrappers
  // --------------------------------------------------------------------------

  advance(toStep: number): Promise<void> {
    return this.callVoid('advanceOnboarding', { toStep });
  }

  updateProfile(displayName: string, ownerType: OnboardingOwnerType): Promise<void> {
    return this.callVoid('updateOnboardingProfile', { displayName, ownerType });
  }

  async checkSlug(slug: string): Promise<SlugAvailabilityResult> {
    const workspaceId = this.requireWorkspaceId();
    const fn = httpsCallable<
      { workspaceId: string; slug: string },
      SlugAvailabilityResult
    >(this.fns, 'checkSlugAvailability');
    const res = await fn({ workspaceId, slug });
    return res.data;
  }

  reserveSlug(slug: string): Promise<{ slug: string; currentStep: number }> {
    return this.callWithResult('reserveSlug', { slug });
  }

  createSimulation(input: {
    address: string;
    askingPrice: number;
    expectedRent: number;
    notes?: string;
  }): Promise<{ simulationId: string; currentStep: number }> {
    return this.callWithResult('createSimulationStub', input);
  }

  selectPlan(plan: OnboardingPlan): Promise<void> {
    return this.callVoid('selectOnboardingPlan', { plan });
  }

  complete(durationMs?: number): Promise<void> {
    const skipped = !!this.onboarding()?.skipped;
    return this.callVoid('completeOnboarding', {
      ...(durationMs != null ? { durationMs } : {}),
      ...(skipped ? { skipped: true } : {}),
    });
  }

  skip(fromStep: number): Promise<void> {
    return this.callVoid('skipOnboarding', { fromStep });
  }

  async relaunch(workspaceId?: string): Promise<void> {
    await this.authReady.whenAuthReady();
    const targetId = workspaceId ?? this.requireWorkspaceId();
    const fn = httpsCallable(this.fns, 'relaunchOnboarding');
    await fn({ workspaceId: targetId });
  }

  /**
   * Optimistically flip `onboarding.completed` on the locally cached
   * workspace so the `OnboardingCompletedGuard` lets the user through
   * immediately when we navigate away from /onboarding. The Firestore
   * listener will overwrite the signal with the authoritative state once
   * the callable response lands.
   *
   * `skipped` lets us distinguish "finished" from "dismissed" in analytics
   * code reading the signal before the server catches up.
   */
  markLocallyFinished(opts: { skipped?: boolean } = {}): void {
    try {
      sessionStorage.setItem(ONBOARDING_JUST_FINISHED_KEY, '1');
    } catch {
      /* storage quotas / SSR — safe to ignore */
    }
    const ws = this.workspaceStore.currentWorkspace();
    if (!ws) return;
    const now = new Date();
    const previous = ws.onboarding;
    const nextOnboarding: WorkspaceOnboarding = previous
      ? {
          ...previous,
          completed: true,
          completedAt: previous.completedAt ?? now,
          ...(opts.skipped
            ? { skipped: true, skippedAt: previous.skippedAt ?? now }
            : {}),
        }
      : {
          completed: true,
          completedAt: now,
          currentStep: ONBOARDING_STEP_COUNT - 1,
          startedAt: now,
          showDashboardTour: !opts.skipped,
          schemaVersion: 1,
          ...(opts.skipped ? { skipped: true, skippedAt: now } : {}),
        };
    this.workspaceStore.setCurrentWorkspace({
      ...ws,
      onboarding: nextOnboarding,
    });
  }

  /**
   * Optimistically patch the locally cached `workspace.onboarding` so the UI
   * reflects a change (e.g. the freshly typed workspace name on the summary
   * screen) before the Firestore listener catches up with the durable write.
   * No-ops when no workspace / onboarding doc is loaded yet.
   */
  patchOnboardingLocal(partial: Partial<WorkspaceOnboarding>): void {
    const ws = this.workspaceStore.currentWorkspace();
    if (!ws?.onboarding) return;
    this.workspaceStore.setCurrentWorkspace({
      ...ws,
      onboarding: { ...ws.onboarding, ...partial },
    });
  }

  // --------------------------------------------------------------------------
  // Durable background writes — power the optimistic steps. The component
  // navigates immediately (optimistic) and fires one of these; they own their
  // own error handling so they can safely outlive the component that started
  // them (the user has already moved on). A transient failure is retried; a
  // persistent one surfaces a recoverable snackbar (app-level, so it shows on
  // the destination route) rather than silently dropping the write — which is
  // what used to bounce users back into onboarding on their next visit.
  // --------------------------------------------------------------------------

  /** Optimistic local name patch + durable background profile write. */
  flushUpdateProfile(displayName: string, ownerType: OnboardingOwnerType): void {
    this.patchOnboardingLocal({ displayName, ownerType });
    void this.runDurable(() => this.updateProfile(displayName, ownerType));
  }

  /** Durable background step advance (monotonic server-side, safe to retry). */
  flushAdvance(toStep: number): void {
    void this.runDurable(() => this.advance(toStep));
  }

  /** Durable background completion write. */
  flushComplete(durationMs?: number): void {
    void this.runDurable(() => this.complete(durationMs));
  }

  /** Durable background simulation-stub write. */
  flushCreateSimulation(input: {
    address: string;
    askingPrice: number;
    expectedRent: number;
    notes?: string;
  }): void {
    void this.runDurable(() => this.createSimulation(input).then(() => undefined));
  }

  private async runDurable(
    op: () => Promise<unknown>,
    attemptsLeft = 2,
  ): Promise<void> {
    try {
      await op();
    } catch (err) {
      if (attemptsLeft > 1) {
        // Brief backoff, then retry once — covers the common transient hiccup.
        await new Promise<void>((resolve) => setTimeout(resolve, 700));
        return this.runDurable(op, attemptsLeft - 1);
      }
      console.error('[onboarding] durable write failed after retries', err);
      this.uiFeedback.toast.action(
        this.translate.instant('onboarding.shell.errors.saveFailed'),
        this.translate.instant('onboarding.shell.retry'),
        () => void this.runDurable(op, 2),
        { type: 'error', duration: 8000 },
      );
    }
  }

  // --------------------------------------------------------------------------
  // Pure client-side slug validation (fast pre-check before the callable).
  // Duplicates server logic intentionally — UX polish, not authority. The
  // server applies the same `sanitizeOnboardingSlug` defensively before
  // running its own regex/uniqueness checks, so what the user sees here is
  // exactly what gets reserved.
  // --------------------------------------------------------------------------

  /**
   * Strip invalid characters as the user types. Lowercases, removes
   * diacritics (é → e), maps `.` `_` and whitespace to `-`, drops anything
   * else, collapses hyphen runs and trims edges. Does NOT enforce min/max
   * length — the form control validators do.
   */
  sanitizeSlug(raw: string): string {
    return sanitizeOnboardingSlug(raw);
  }

  classifySlugLocally(slug: string): SlugAvailabilityResult {
    const normalized = this.sanitizeSlug(slug);
    if (!ONBOARDING_SLUG_REGEX.test(normalized)) {
      return { available: false, reason: 'invalid' };
    }
    if (isReservedOnboardingSlug(normalized, ONBOARDING_RESERVED_SLUGS)) {
      return { available: false, reason: 'reserved' };
    }
    return { available: true };
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  private requireWorkspaceId(): string {
    const id = this.workspaceStore.currentWorkspaceId();
    if (!id) {
      throw new Error(
        'Onboarding callable invoked before a workspace was active — WorkspaceStore.currentWorkspaceId is null.',
      );
    }
    return id;
  }

  private async callVoid(
    name: string,
    payload: Record<string, unknown>,
  ): Promise<void> {
    await this.authReady.whenAuthReady();
    const workspaceId = this.requireWorkspaceId();
    const fn = httpsCallable(this.fns, name);
    await fn({ workspaceId, ...payload });
  }

  private async callWithResult<R>(
    name: string,
    payload: Record<string, unknown>,
  ): Promise<R> {
    await this.authReady.whenAuthReady();
    const workspaceId = this.requireWorkspaceId();
    const fn = httpsCallable<Record<string, unknown>, R>(this.fns, name);
    const res = await fn({ workspaceId, ...payload });
    return res.data;
  }
}
